import { createActorContext } from '@xstate/react';
import { useQueryClient } from '@tanstack/react-query';
import {
    ServiceShortName,
    TrackingAction,
    TrackingActionCategory,
} from '@acp/shared';
import { useToast } from '@chakra-ui/react';
import { addTrackingEvent } from '../../../../../hooks/use-tracking-event';
import { reportedFindingsWizardMachine } from './machine';

const ReportedFindingsMachineContext = createActorContext(reportedFindingsWizardMachine);

function ReportedFindingsWizardProvider({
    compliancePropertyId,
    serviceShortName,
    children,
}: {
    compliancePropertyId: number;
    serviceShortName: ServiceShortName | string;
    children: React.ReactNode;
}) {
    const toast = useToast();
    // TODO: will have to modify this as we go with building the UI
    const queryClient = useQueryClient();

    const invalidCache = () => {
        // Invalidate queries associated with reported findings
        void queryClient.invalidateQueries({
            queryKey: ['property', compliancePropertyId],
        });

        void queryClient.invalidateQueries({
            queryKey: ['reported-findings'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['property', compliancePropertyId, 'reported-findings'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['minimum-standards', 'list'],
        });
    };

    const onAnalyticsHandler = (
        data: Record<string, unknown>,
        trackingType: TrackingAction
    ) => {
        addTrackingEvent({
            category: TrackingActionCategory.ReportedFindingsWizard,
            action: trackingType,
            context: {
                compliancePropertyId,
                ...data,
            },
        });
    };

    const handleFatalError = (message: string) => {
        addTrackingEvent({
            category: TrackingActionCategory.ReportedFindingsWizard,
            action: TrackingAction.ReportedFindingsWizardFatalError,
            context: {
                compliancePropertyId,
                message,
            },
        });
        invalidCache();
    };

    return (
        <ReportedFindingsMachineContext.Provider
            logic={reportedFindingsWizardMachine.provide({
                actions: {
                    onAddTracking:
                    (_, params) => onAnalyticsHandler(
                        params.data,
                        params.trackingType
                    ),
                    onFatalError: (_, params) => handleFatalError(
                        params.message
                    ),
                    resetCache: invalidCache,
                    onToast: (_, { description, title, status }) => {
                        toast({
                            title,
                            description,
                            status,
                            isClosable: true,
                        });
                    },
                },
            })}
            options={{
                input: {
                    compliancePropertyId,
                    serviceShortName,
                },
            }}
        >
            {children}
        </ReportedFindingsMachineContext.Provider>
    );
}

export { ReportedFindingsWizardProvider, ReportedFindingsMachineContext };
